import { Fragment, useMemo, useState } from "react";
import {
  CmwCheckIcon,
  CmwDurationIcon,
  CmwHeartPlusIcon,
  CmwMedicalIcon,
  CmwRecipientIcon,
  CmwServiceIcon,
  CmwTrustShieldIcon,
} from "./CareMatchingWizardIcons";
import "./CareMatchingWizard.css";

const RECIPIENTS = [
  { id: "elderly_parent", title: "Elderly Parent", desc: "Care for aging parents", icon: "elderly_parent" },
  { id: "post_surgery", title: "Post Surgery", desc: "Recovery after surgery", icon: "post_surgery" },
  { id: "disabled", title: "Disabled", desc: "Special-needs care", icon: "disabled" },
  { id: "recovery_patient", title: "Recovery Patient", desc: "Illness or injury recovery", icon: "recovery_patient" },
];

const DURATIONS = [
  { id: "hourly", title: "Hourly", desc: "Care for a few hours as per your need", tag: "Flexible hours", icon: "hourly" },
  { id: "12_day", title: "12 Hours – Day", desc: "Daytime care (7 AM – 7 PM)", tag: "Most Popular", icon: "12_day" },
  { id: "12_night", title: "12 Hours – Night", desc: "Night care (7 PM – 7 AM)", tag: "Peaceful nights", icon: "12_night" },
  { id: "24h", title: "24 Hours", desc: "Round-the-clock care and support", tag: "Full-time support", icon: "24h" },
];

const SERVICES = [
  {
    id: "attendant",
    title: "Care Attendant",
    desc: "Daily living assistance, personal care, and companionship for your loved ones.",
    tag: "Daily living support",
    tone: "blue",
  },
  {
    id: "nurses",
    title: "Nurses",
    desc: "Professional nursing care including medication management and health monitoring.",
    tag: "Medical & clinical care",
    tone: "green",
  },
  {
    id: "physio",
    title: "Physiotherapist",
    desc: "Physical therapy and rehabilitation to improve mobility and strength.",
    tag: "Rehab & mobility",
    tone: "purple",
  },
  {
    id: "elder_companion",
    title: "Elder Companion",
    desc: "Friendly companionship, conversation, and social engagement for patients.",
    tag: "Companionship & support",
    tone: "pink",
  },
];

const STEPPER = [
  { id: 1, label: "Who needs care?" },
  { id: 2, label: "Duration & Shifts" },
  { id: 3, label: "Care Requirements" },
];

const TRUST_BADGES = [{ id: "nema", label: "NCID Identity Verified", tone: "blue" }];

function SelectBadge() {
  return (
    <span className="cmw-select-badge" aria-hidden>
      <CmwCheckIcon />
    </span>
  );
}

function CircleStepper({ step }) {
  return (
    <div className="cmw-stepper" aria-label={`Step ${step} of 3`}>
      {STEPPER.map((item, index) => {
        const done = step > item.id;
        const active = step === item.id;
        return (
          <Fragment key={item.id}>
            {index > 0 ? <span className="cmw-stepper__line" aria-hidden /> : null}
            <div className="cmw-stepper__item">
              <div className={`cmw-stepper__node${active ? " is-active" : ""}${done ? " is-done" : ""}`}>
                {done ? <CmwCheckIcon /> : item.id}
              </div>
              <span className={`cmw-stepper__label${active ? " is-active" : ""}`}>{item.label}</span>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}

function TrustBanner() {
  return (
    <div className="cmw-trust-banner">
      <span className="cmw-trust-banner__icon">
        <CmwTrustShieldIcon tone="blue" />
      </span>
      <p>Review NCID identity-verification information on care professional profiles before making a hiring decision.</p>
      <button type="button" className="cmw-trust-banner__link">
        Learn more
      </button>
    </div>
  );
}

function TrustBadges() {
  return (
    <div className="cmw-trust-badges">
      {TRUST_BADGES.map((badge) => (
        <div key={badge.id} className="cmw-trust-badges__item">
          <span className={`cmw-trust-badges__icon cmw-trust-badges__icon--${badge.tone}`}>
            {badge.medical ? <CmwMedicalIcon /> : <CmwTrustShieldIcon tone={badge.tone} />}
          </span>
          {badge.label}
        </div>
      ))}
    </div>
  );
}

export default function CareMatchingWizard({
  onComplete,
  enabledCareTypes = ["attendant", "nurses", "physio"],
  enabledServiceTypes = ["hourly", "12_day", "12_night", "24h"],
}) {
  const [step, setStep] = useState(1);
  const [recipient, setRecipient] = useState("elderly_parent");
  const [duration, setDuration] = useState(() => (enabledServiceTypes.includes("12_day") ? "12_day" : (enabledServiceTypes[0] || "hourly")));
  const [services, setServices] = useState(() => new Set(["attendant"]));

  const canAdvance = useMemo(() => {
    if (step === 1) return Boolean(recipient);
    if (step === 2) return Boolean(duration);
    return services.size > 0;
  }, [step, recipient, duration, services]);

  function toggleService(id) {
    setServices((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        if (next.size > 1) next.delete(id);
      } else {
        next.add(id);
      }

      // Requirement: Physiotherapist (physio) only supports the "Hourly" service type.
      if (next.has("physio")) setDuration(enabledServiceTypes.includes("hourly") ? "hourly" : "");
      return next;
    });
  }

  function handleNext() {
    if (!canAdvance) return;
    if (step < 3) {
      setStep((s) => s + 1);
      return;
    }
    // Defensive: enforce physio -> hourly even if the user edits step order.
    const finalDuration = services.has("physio")
      ? enabledServiceTypes.includes("hourly")
        ? "hourly"
        : duration
      : duration;
    if (finalDuration !== duration) setDuration(finalDuration);
    const careTypes = [...services].map((id) => (id === "elder_companion" ? "attendant" : id));
    onComplete?.({ recipient, duration: finalDuration, careTypes });
  }

  function handleBack() {
    if (step > 1) setStep((s) => s - 1);
  }

  return (
    <section className="cmw" aria-labelledby="cmw-title">
      <div className="cmw__head">
        <span className="cmw__head-icon" aria-hidden>
          <CmwHeartPlusIcon />
        </span>
        <h2 id="cmw-title" className="cmw__title">
          Not sure what <em>care</em> you need?
        </h2>
        <p className="cmw__lead">
          Our care-matching wizard finds the right support in under 2 minutes.
        </p>
      </div>

      <div className="cmw__panel">
        {step > 1 ? (
          <button type="button" className="cmw__back-top" onClick={handleBack}>
            <svg viewBox="0 0 20 20" fill="none" aria-hidden>
              <path d="m12 5-5 5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back
          </button>
        ) : null}

        <CircleStepper step={step} />

        {step === 1 ? (
          <>
            <div className="cmw__question">
              <h3>Who needs care?</h3>
              <p>Select the option that best describes your situation.</p>
            </div>
            <div className="cmw-recipient-grid">
              {RECIPIENTS.map((item) => {
                const selected = recipient === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`cmw-choice-card${selected ? " is-selected" : ""}`}
                    onClick={() => setRecipient(item.id)}
                  >
                    {selected ? <SelectBadge /> : null}
                    <span className="cmw-choice-card__icon">
                      <CmwRecipientIcon name={item.icon} />
                    </span>
                    <strong>{item.title}</strong>
                    <span>{item.desc}</span>
                  </button>
                );
              })}
            </div>
          </>
        ) : null}

        {step === 2 ? (
          <>
            <div className="cmw__question cmw__question--center">
              <h3>How long and when do you need care?</h3>
              <p>Choose the duration and shift that best fits your needs.</p>
            </div>
            <div className="cmw-duration-grid">
              {DURATIONS.filter((item) => {
                if (services.has("physio")) return item.id === "hourly" && enabledServiceTypes.includes("hourly");
                return enabledServiceTypes.includes(item.id);
              }).map((item) => {
                const selected = duration === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`cmw-choice-card cmw-choice-card--duration${selected ? " is-selected" : ""}`}
                    onClick={() => setDuration(item.id)}
                  >
                    {selected ? <SelectBadge /> : null}
                    <span className="cmw-choice-card__icon">
                      <CmwDurationIcon name={item.icon} />
                    </span>
                    <strong>{item.title}</strong>
                    <span>{item.desc}</span>
                    <em className="cmw-choice-card__tag">{item.tag}</em>
                  </button>
                );
              })}
            </div>
          </>
        ) : null}

        {step === 3 ? (
          <>
            <div className="cmw__question cmw__question--center">
              <h3>What type of care do you need?</h3>
              <p>Select one or more services that best match your requirements.</p>
            </div>
            <div className="cmw-service-grid">
              {SERVICES.filter((item) => {
                const id = item.id;
                if (id === "elder_companion") return enabledCareTypes.includes("attendant");
                return enabledCareTypes.includes(id);
              }).map((item) => {
                const checked = services.has(item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`cmw-service-card cmw-service-card--${item.tone}${checked ? " is-selected" : ""}`}
                    onClick={() => toggleService(item.id)}
                  >
                    <span className={`cmw-service-card__check${checked ? " is-checked" : ""}`} aria-hidden>
                      {checked ? <CmwCheckIcon /> : null}
                    </span>
                    <span className={`cmw-service-card__icon cmw-service-card__icon--${item.tone}`}>
                      <CmwServiceIcon name={item.id} />
                    </span>
                    <div className="cmw-service-card__copy">
                      <strong>{item.title}</strong>
                      <p>{item.desc}</p>
                      <em className={`cmw-service-card__tag cmw-service-card__tag--${item.tone}`}>{item.tag}</em>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        ) : null}

        <div className={`cmw__nav${step === 1 ? " cmw__nav--solo" : ""}`}>
          {step > 1 ? (
            <button type="button" className="cmw__btn cmw__btn--ghost" onClick={handleBack}>
              <svg viewBox="0 0 20 20" fill="none" aria-hidden>
                <path d="m12 5-5 5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Previous
            </button>
          ) : (
            <span aria-hidden />
          )}
          <button type="button" className="cmw__btn cmw__btn--primary" disabled={!canAdvance} onClick={handleNext}>
            {step === 3 ? "Show Matches" : "Next Step"}
            <svg viewBox="0 0 20 20" fill="none" aria-hidden>
              <path d="m8 5 5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {step > 1 ? <TrustBanner /> : null}
      </div>

      {step > 1 ? <TrustBadges /> : null}
    </section>
  );
}
